"use strict";
/**
 * Delegation nodes are used within the KILT protocol to construct the trust hierarchy.
 *
 * Starting from the root node, entities can delegate the right to issue attestations to Claimers for a certain CTYPE and also delegate the right to attest and to delegate further nodes.
 *
 * @packageDocumentation
 * @module DelegationNode
 * @preferred
 */
Object.defineProperty(exports, "__esModule", { value: true });
const tslib_1 = require("tslib");
const utils_1 = require("@kiltprotocol/utils");
const config_1 = require("@kiltprotocol/config");
const Delegation_1 = tslib_1.__importDefault(require("./Delegation"));
const DelegationNode_chain_1 = require("./DelegationNode.chain");
const DelegationNodeUtils = tslib_1.__importStar(require("./DelegationNode.utils"));
const DelegationRootNode_chain_1 = require("./DelegationRootNode.chain");
const log = config_1.ConfigService.LoggingFactory.getLogger('DelegationNode');
class DelegationNode extends Delegation_1.default {
    /**
     * Creates a new [DelegationNode].
     *
     * @param delegationNodeInput - The base object from which to create the delegation node.
     */
    constructor(delegationNodeInput) {
        super(delegationNodeInput);
        this.permissions = delegationNodeInput.permissions;
        this.rootId = delegationNodeInput.rootId;
        this.parentId = delegationNodeInput.parentId;
        DelegationNodeUtils.errorCheck(this);
    }
    /**
     * [STATIC] Queries the delegation node with [delegationId].
     *
     * @param delegationId The unique identifier of the desired delegation.
     * @returns Promise containing the [[DelegationNode]] or [null].
     */
    static async query(delegationId) {
        log.info(`:: query('${delegationId}')`);
        const result = await DelegationNode_chain_1.query(delegationId);
        log.info(`result: ${JSON.stringify(result)}`);
        return result;
    }
    /**
     *
     * Generates the delegation hash from the delegations' property values.
     *
     * This hash is signed by the delegate and later stored along with the delegation to
     * make sure delegation data (such as permissions) is not tampered.
     *
     * @example
     * ```
     * // Sign the hash of the delegation node...
     * const delegate: Identity = ...
     * const signature:string = delegate.signStr(newDelegationNode.generateHash())
     *
     * // Store the signed hash on the Kilt chain...
     * const myIdentity: Identity = ...
     * tx = newDelegationNode.store(signature)
     * BlockchainUtils.signAndSendTx(tx, myIdentity)
     * ```
     *
     * @returns The hash representation of this delegation **as a hex string**.
     */
    generateHash() {
        const propsToHash = [this.id, this.rootId];
        if (this.parentId && this.parentId !== this.rootId) {
            propsToHash.push(this.parentId);
        }
        const uint8Props = propsToHash.map((value) => {
            return utils_1.Crypto.coToUInt8(value);
        });
        uint8Props.push(DelegationNodeUtils.permissionsAsBitset(this));
        const generated = utils_1.Crypto.u8aToHex(utils_1.Crypto.hash(utils_1.Crypto.u8aConcat(...uint8Props), 256));
        log.debug(`generateHash(): ${generated}`);
        return generated;
    }
    /**
     * [ASYNC] Fetches the root of this delegation node.
     *
     * @throws [[ERROR_ROOT_NODE_QUERY]] when the rootId could not be queried.
     * @returns Promise containing the [[DelegationRootNode]] of this delegation node.
     */
    async getRoot() {
        const rootNode = await DelegationRootNode_chain_1.query(this.rootId);
        if (!rootNode) {
            throw utils_1.SDKErrors.ERROR_ROOT_NODE_QUERY(this.rootId);
        }
        return rootNode;
    }
    /**
     * [ASYNC] Fetches the parent node of this delegation node.
     *
     * @returns Promise containing the parent as [[DelegationBaseNode]] or [null].
     */
    async getParent() {
        if (!this.parentId || this.parentId === this.rootId) {
            // parent must be root
            return this.getRoot();
        }
        return DelegationNode_chain_1.query(this.parentId);
    }
    /**
     * [ASYNC] Stores the delegation node on chain.
     *
     * @param signature Signature of the delegate to ensure it is done under the delegate's permission.
     * @returns Promise containing a unsigned SubmittableExtrinsic.
     */
    async store(signature) {
        log.info(`:: store(${this.id})`);
        return DelegationNode_chain_1.store(this, signature);
    }
    /**
     * [ASYNC] Verifies the delegation node by querying it from chain and checking its revocation status.
     *
     * @returns Promise containing a boolean flag.
     */
    async verify() {
        const node = await DelegationNode_chain_1.query(this.id);
        return node !== null && !node.revoked;
    }
    /**
     * [ASYNC] Revokes the delegation node on chain.
     *
     * @param address The address of the identity used to revoke the delegation.
     * @returns Promise containing an unsigned SubmittableExtrinsic.
     */
    async revoke(address) {
        const { steps, node } = await this.findAncestorOwnedBy(address);
        if (!node) {
            throw utils_1.SDKErrors.ERROR_UNAUTHORIZED(`Identity with address ${address} is not among the delegators and may not revoke this node`);
        }
        const childCount = await this.subtreeNodeCount();
        // must revoke all children and self
        const revocationCount = childCount + 1;
        log.debug(`:: revoke(${this.id}) with maxRevocations=${revocationCount} and maxDepth = ${steps} through delegation node ${node === null || node === void 0 ? void 0 : node.id} and identity ${address}`);
        return DelegationNode_chain_1.revoke(this.id, steps, revocationCount);
    }
    async getChildren() {
        return DelegationNode_chain_1.getChildren(this.id);
    }
}
exports.default = DelegationNode;
